/*!
 * pagination.js v1.0.0
 * 
 * Handles Materialize.js pagination component behaviour (creation of the pagination elements, handling of page 
 * changing events and filtering of displayed pages elements)
 * 
 * Depends on jQuery
 *
 * Copyright 2019 - Momentum Technologies/Manitou Solution
 * Released under the MIT license
 * 
 * Based on https://github.com/pinzon1992/materialize_table_pagination
 * Licence: https://github.com/pinzon1992/materialize_table_pagination/blob/master/LICENSE
 *
 * Date: 2019-01-29
 * Author: Martin Lessard
 */

import $ from 'jquery';

 /*!
 * ---------------------------------------------------------------------------------------------------------------------
 * OPTIONS
 * ---------------------------------------------------------------------------------------------------------------------
 * {showPaginationNumbers} : Display or not the clickable pagination numbers.
 * {showPaginationArrows} : Display or not the clickable pagination arrows (previous < and next >).
 * {showItemCount} : Display or not the total number of items handled by the pagination into an element with the class 
 *                   specified by {totalItemCountSelector}.
 * {showItemRange} : Display or not the range of displayed items in the currently selected page into elements with the 
 *                   class specified by {lowerItemCountSelector} and {upperItemCountSelector}.
 * {itemsPerPage} : The number of items to display per page.
 * {paginationSelector} : The selector for the pagination element.
 * {itemsContainerSelector} : The selector for the items container element.
 * {itemsSelector} : The selector for the items to filter within the items container element.
 * {totalItemCountSelector} : The selector for the element used to display the total item count (Only used if 
 *                            {showItemCount} is true).
 * {lowerItemCountSelector} : The selector for the element used to display the lower bracket of the items range 
 *                            displayed in the current page (Only used if {showItemRange} is true).
 * {upperItemCountSelector} : The selector for the element used to display the upper bracket of the items range 
 *                            displayed in the current page (Only used if {showItemRange} is true).
 * {activeColor} : The color of the active page number.
 * {nextTooltipText} : The tooltip text used for the next button.
 * {prevTooltipText} : The tooltip text used for the previous button.
 * 
 */

$.fn.manitouPagination = function(options){
    var $this = this;
    var defaultSettings = {
        showPaginationNumbers: true,
        showPaginationArrows: true,
        showItemCount: false,
        showItemRange: false,
        itemsPerPage: 10,
        paginationSelector: '.pagination',
        itemsContainerSelector: 'tbody',
        itemsSelector: 'tr',
        totalItemCountSelector: '.total-item-count',
        lowerItemCountSelector: '.lower-item-count',
        upperItemCountSelector: '.upper-item-count',
        activeColor: 'black',
        nextTooltipText: '',
        prevTooltipText: ''
    };
    var settings = $.extend(defaultSettings, options);

    var itemsPerPage = settings.itemsPerPage;
    var itemsContainerElement = $this.find(settings.itemsContainerSelector);
    var itemsElements = itemsContainerElement.find(settings.itemsSelector);
    var paginationElement = $(settings.paginationSelector);

    var itemCount = itemsElements.length;
    var pageCount = Math.ceil(itemCount / itemsPerPage);
    
    // Display the total item count
    if (settings.showItemCount) {
        $(settings.totalItemCountSelector).html(itemCount);
    }

    // Display the item range of the first and currently selected page (ex: 1 to 10)
    if (settings.showItemRange) {
        $(settings.lowerItemCountSelector).html(1);
        $(settings.upperItemCountSelector).html(Math.min(itemCount, itemsPerPage));
    }

    paginationElement.data('current-page', 1);

    // Add pagination left (previous) arrow
    if (settings.showPaginationArrows){
        var previousArrowHtml =
            '<li>' +
                '<a href="#" class="manitouPreviousLink" title="' + settings.prevTooltipText + '">' +
                    '<i class="material-icons">chevron_left</i>' +
                '</a>' +
            '</li>';
        $(previousArrowHtml).appendTo(paginationElement);
    }

    // Add pagination numbers
    if (settings.showPaginationNumbers) {
        for (var pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
            var pageNumberHtml =
                '<li class="waves-effect manitouPageNumber hide-on-small-only" data-page="' + pageNumber + '">' +
                    '<a href="#" class="manitouPageLink">' + pageNumber + '</a>' +
                '</li>';
            $(pageNumberHtml).appendTo(paginationElement);
        }
    }

    // Add pagination right (next) arrow
    if (settings.showPaginationArrows){
        var nextArrowHtml =
            '<li>' +
                '<a href="#" class="manitouNextLink" title="' + settings.nextTooltipText + '">' +
                    '<i class="material-icons">chevron_right</i>' +
                '</a>' +
            '</li>';
        $(nextArrowHtml).appendTo(paginationElement);
    }

    // Display the first page number as active
    paginationElement.find('.manitouPageNumber').filter(function () {
        return $(this).data('page') == 1;
    }).addClass('active '  + settings.activeColor);
    // Diasable the previous arrow since the first page is selected
    paginationElement.find('.manitouPreviousLink').parent().addClass('disabled');
    // Disable the next arrow if there is only 1 page
    if (pageCount <= 1) {
        paginationElement.find('.manitouNextLink').parent().addClass('disabled');
    }

    // Show only the first [itemsPerPage] items
    itemsElements.hide();
    itemsElements.slice(0, itemsPerPage).show();

    // Set event listeners for pagination arrows and numbers
    paginationElement.find('.manitouPageLink').click(function() {
        var clickedPageNumber = $(this).html().valueOf();
        goTo(clickedPageNumber);
        return false;
    });
    paginationElement.find('.manitouPreviousLink').click(function() {
        if (!$(this).parent().hasClass('disabled')) {
            previous();
        }
        return false;
    });
    paginationElement.find('.manitouNextLink').click(function() {
        if (!$(this).parent().hasClass('disabled')) {
            next();
        }
        return false;
    });

    // Go to previous page
    function previous(){
        var previousPageNumber = parseInt(paginationElement.data('current-page')) - 1;
        goTo(previousPageNumber);
    }

    // Go to next page
    function next(){
        var nextPageNumber = parseInt(paginationElement.data('current-page')) + 1;
        goTo(nextPageNumber);
    }

    // Display the items of the page in parameter according to the [itemsPerPage] setting
    function goTo(pageNumber){
        var startAt = (pageNumber - 1) * itemsPerPage;
        var endAt = startAt + itemsPerPage;

        // Display the item range of the newly selected page (ex: 11 to 18)
        if (settings.showItemRange) {
            $(settings.lowerItemCountSelector).html(startAt + 1);
            $(settings.upperItemCountSelector).html(Math.min(itemCount, endAt));
        }

        // Display only the items of the selected page
        itemsElements.hide().slice(startAt, endAt).show();

        // Disable the previous arrow if we are on the first page
        if (pageNumber > 1) {
            paginationElement.find('.manitouPreviousLink').parent().removeClass('disabled');
        } else {
            paginationElement.find('.manitouPreviousLink').parent().addClass('disabled');
        }
        // Disable the next arrow if we are on the last page
        if (pageNumber < pageCount) {
            paginationElement.find('.manitouNextLink').parent().removeClass('disabled');
        } else {
            paginationElement.find('.manitouNextLink').parent().addClass('disabled');
        }

        // Change the active pagination page number
        paginationElement.data('current-page', pageNumber);
        paginationElement.find('.manitouPageNumber').removeClass('active ' + settings.activeColor);
        paginationElement.find('.manitouPageNumber').filter(function () {
            return $(this).data('page') == pageNumber;
        }).addClass('active '  + settings.activeColor);
    }
};